import React, { useState } from "react";

interface InputFieldProps {
  label: string;
  placeholder: string;
  width?: string;
  onChange: (value: string) => void;
}

const InputField: React.FC<InputFieldProps> = ({
  label,
  placeholder,
  width,
  onChange,
}) => (
  <div className={`flex flex-col ${width || "w-[50%]"} p-4`}>
    <label className="font-bold">{label}</label>
    <input
      type="text"
      placeholder={placeholder}
      className="border border-gray-300 rounded-md px-3 py-2 mt-1 mb-4 focus:outline-none focus:ring-2 focus:ring-indigo-400 flex-grow placeholder-normal"
      onChange={(e) => onChange(e.target.value)}
    />
  </div>
);

interface ContactItemProps {
  icon: string;
  title: string;
  content: React.ReactNode;
}

const ContactItem: React.FC<ContactItemProps> = ({ icon, title, content }) => (
  <div className="flex flex-col w-[33%] max-md:ml-0 max-md:w-full">
    <div className="flex flex-col grow mt-2.5 tracking-tight text-gray-900 max-md:mt-10">
      <div className="flex gap-5 text-2xl font-bold leading-9">
        <img src={icon} alt="" className="shrink-0 aspect-square w-[54px]" />
        <h3 className="flex-auto self-start">{title}</h3>
      </div>
      <div className="pr-2px flex-auto self-start text-xl leading-8 px-4 w-auto">
        {content}
      </div>
    </div>
  </div>
);

const ContactUsForm: React.FC = () => {
  const [formData, setFormData] = useState({
    firstName: "",
    email: "",
    phoneNumber: "",
    subject: "",
    message: "",
  });

  const handleChange = (name: string, value: string) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const phoneRegex = /^\+?\d{1,3}(?:[-\s]?\d{3}){3}$/;

    if (!emailRegex.test(formData.email)) {
      alert("Please enter a valid email.");
      return;
    }

    if (!phoneRegex.test(formData.phoneNumber)) {
      alert("Please enter a valid phone number.");
      return;
    }

    try {
      const res = await fetch("https://your-backend-endpoint.com/submit", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });

      if (!res.ok) throw new Error("Form submit failed.");

      alert("Form submitted successfully!");
      setFormData({
        firstName: "",
        email: "",
        phoneNumber: "",
        subject: "",
        message: "",
      });
    } catch (err) {
      console.error("Error:", err);
      alert("Submission failed. Try again later.");
    }
  };

  const contactData = [
    {
      icon: "https://cdn.builder.io/api/v1/image/assets/TEMP/18179b6ac038d84423d4ee4c96386281587fa212096dec52fd422ee065082649?apiKey=demo",
      title: "Call us",
      content: (
        <>
          +1-940-394-2948
          <br />
          +1-389-385-3807
        </>
      ),
    },
    {
      icon: "https://cdn.builder.io/api/v1/image/assets/TEMP/c9791065adc6c16870ff4a2dc0b81db91ca10e8583d26b74df0f342958a2a420?apiKey=demo",
      title: "Email us",
      content: (
        <>
          support@brainwave.io
          <br />
          contact@brainwave.io
        </>
      ),
    },
    {
      icon: "https://cdn.builder.io/api/v1/image/assets/TEMP/5f06f2bccb9b07bf8a8046586bd9f211dbb71be297a47e5dd6d80a6ee9193342?apiKey=demo",
      title: "Visit us",
      content: (
        <>
          34 Madison Street,
          <br />
          NY, USA 10005
        </>
      ),
    },
  ];

  return (
    <div className="container mx-auto py-20 px-6">
      <div className="bg-[#F4F7FA] px-10 py-10 max-w-[1250px] w-full rounded-2xl mx-auto">
        {/* Heading */}
        <section className="flex flex-col text-center text-gray-900 items-center">
          <h1 className="text-5xl font-bold leading-tight max-md:text-4xl pt-8">
            Contact Us
          </h1>
          <p className="mt-8 text-lg max-w-xl">
            With lots of unique blocks, you can easily build a page without
            coding. Build your next consultancy website within few minutes.
          </p>
        </section>

        {/* Contact Info */}
        <section className="py-20">
          <div className="flex gap-8 max-md:flex-col">
            {contactData.map((item, i) => (
              <ContactItem
                key={i}
                icon={item.icon}
                title={item.title}
                content={item.content}
              />
            ))}
          </div>
        </section>

        {/* Form */}
        <section className="bg-white px-6 py-10 rounded-xl shadow-2xl">
          <div className="flex flex-wrap justify-center">
            <div className="flex w-full max-w-[1000px] justify-between flex-wrap">
              <InputField
                label="Full Name"
                placeholder="e.g. John Smith"
                width="w-[48%]"
                onChange={(val) => handleChange("firstName", val)}
              />
              <InputField
                label="Email"
                placeholder="e.g. john@mail.com"
                width="w-[48%]"
                onChange={(val) => handleChange("email", val)}
              />
              <InputField
                label="Phone Number"
                placeholder="e.g. +1-234-567-7890"
                width="w-[48%]"
                onChange={(val) => handleChange("phoneNumber", val)}
              />
              <InputField
                label="Subject"
                placeholder="e.g. I need help"
                width="w-[48%]"
                onChange={(val) => handleChange("subject", val)}
              />
              <InputField
                label="Message"
                placeholder="Type your message here"
                width="w-full"
                onChange={(val) => handleChange("message", val)}
              />
            </div>
            <div className="w-full text-left pt-8 pl-4">
              <button
                onClick={handleSubmit}
                className="text-white bg-indigo-600 hover:bg-indigo-700 px-8 py-3 rounded-lg text-lg"
              >
                Send Message
              </button>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default ContactUsForm;
